import { MILLISECONDS_IN_DAY } from '../constants/Misc'
import { ArrayUtils } from './ArrayUtils'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from './GlobalFunctions'
import { isJSONString } from './JSONUtils'
import { localStorageSet$ } from '../common/RxJS/LocalStorageState'

/**
 * Cookie Utils.
 */
const ESSENTIAL_COOKIES: string[] = ['JSESSIONID']

const getCookies = (): string => document.cookie

export const getCookiesAsArray = (): [string, string][] => {
    const cookies: string = getCookies()

    if (IsStringEmpty(cookies)) {
        return []
    }

    return cookies.split('; ').map((v: string) => v.split('=').map(decodeURIComponent)) as [string, string][]
}
export const getCookiesAsMap = (): Record<string, string> => ArrayUtils.createObjectFromArray(getCookiesAsArray())
export const getCookie = (name: string): string => getCookiesAsMap()[name]
export const getCookieParsed = <T>(name: string): Nullable<T> => JSON.parse(getCookie(name) ?? 'null') as Nullable<T>

export const hasCookie = (name: string): boolean => getCookie(name) !== undefined

export const setCookie = (name: string, value: unknown, days?: number): void => {
    const date: Date = new Date()

    // Default at one year, in accordance with ePrivacy guidelines.
    const maxAgeDaysInMilliseconds: number = (days ?? 365) * MILLISECONDS_IN_DAY

    date.setTime(date.getTime() + maxAgeDaysInMilliseconds)

    document.cookie = `${name}=${JSON.stringify(value)}; expires=${date.toUTCString()}; path=/`
}

export const deleteCookie = (name: string): void => setCookie(name, '', -1)

export const deleteAllNonEssentialCookies = (): void => {
    Object.keys(getCookiesAsMap()).forEach((key: string) => {
        if (!ESSENTIAL_COOKIES.includes(key)) {
            deleteCookie(key)
        }
    })
}

/**
 * Local Storage Utils.
 */
export type LocalStorageSetterOptions = {
    daysToLive?: number
    version?: string
}

export type LocalStorageItem<T extends unknown> = {
    value: T
    version: string
    expiry: number
}

export class $LocalStorage {
    private static storage: Storage = localStorage

    private static $getItem<T>(key: string, version?: string): Nullable<T> {
        const obj: Nullable<string> = $LocalStorage.storage.getItem(key)

        if (!obj || !isJSONString(obj)) return null

        const parsed: T = JSON.parse(obj) as T

        if (typeof parsed !== 'object') return parsed

        const lsItem: LocalStorageItem<unknown> = parsed as Record<string, unknown> as LocalStorageItem<unknown>

        if (!IsVarEmpty(lsItem?.version) && !IsVarEmpty(version)) {
            if (
                !~lsItem?.version?.localeCompare(version, undefined, {
                    numeric: true,
                    sensitivity: 'base'
                })
            ) {
                return null
            }
        }

        if (!IsVarEmpty(lsItem?.expiry) && !IsVarEmpty(lsItem?.value)) {
            const { expiry, value }: LocalStorageItem<unknown> = lsItem
            const now: number = new Date().getTime()

            if (now > expiry) {
                $LocalStorage.storage.removeItem(key)
                return null
            }

            return value as T
        }

        return parsed
    }

    private static $setItem(key: string, value: unknown, options?: LocalStorageSetterOptions): void {
        const ttl: Nullable<number> = options?.daysToLive ? options.daysToLive * 86400000 : null
        const now: number = new Date().getTime()
        const item: unknown =
            IsVarEmpty(ttl) && IsVarEmpty(options?.version)
                ? value
                : {
                      value,
                      version: options?.version,
                      expiry: ttl ? now + ttl : undefined
                  }

        $LocalStorage.storage.setItem(key, JSON.stringify(item))
        localStorageSet$.announce(key)
    }

    public static getItem<T>(key: string, version?: string): Nullable<T> {
        return $LocalStorage.$getItem<T>(key, version)
    }

    public static setItem(key: string, value: unknown, options?: LocalStorageSetterOptions): void {
        return $LocalStorage.$setItem(key, value, options)
    }

    public static hasItem(key: string): boolean {
        return isVarNotEmpty($LocalStorage.storage.getItem(key))
    }

    public static removeItem(key: string): void {
        $LocalStorage.storage.removeItem(key)
    }

    public static clear(): void {
        $LocalStorage.storage.clear()
    }
}
